import { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";   // THE RETURN + the invite landing
import { setMatchInviteHandler, useSocialBridge, setFriendActions, setInviteOpener } from "./ieSocial";  // a friend's JOIN, answered without a page load
import { ref, get } from "firebase/database";
import BoosterPack, { PackShopScreen } from "./BoosterPack";
import BootSequence from "./BootSequence";
import Plaza from "./Plaza";
import UnstablePierre from "./UnstablePierre";
import Codex from "./Codex";
import WilliesWorkshop from "./WilliesWorkshop";
import Fauxdex from "./Fauxdex"; // THE POCKET LAW: reads experimentalCards.js, read-only, nothing else does
import WilleesLibrary from "./WilleesLibrary";
import FactionHall from "./FactionHall";
import Dungeon1 from "./Dungeon1";
import WorldMap from "./WorldMap";
import { recordNodeClear, vanguardTierFor, loadWorldProgress, pendingGate, opponentForGate, recordGateClear, recordAmbushClear, recordBeat, recordToolGrant, STORY_PLAYER } from "./worldData";
import { useSoundtrack } from "./Soundtrack";
import { mountMusicPlayer } from "./MusicPlayer";
import { interludeAfter, interludeChain } from "./minerium1"; // story that is not a duel
import { toolWonFromGate } from "./vaelsTools";                // V'ael's Veil — what beating the doorman is worth

import DeckBuilder from "./DeckBuilder";
import Shatterfield from "./Shatterfield";
import Rulebook from "./Rulebook";
import EndlessDepths, { calcEloChange, recordDepthResultLegacy, getRankInfo } from "./EndlessDepths";
import { createMatch, updatePlayer, updateMatch, listenToMatch, drawCards, drawAndDepleteCards } from "./gameManager";
import TradePost from "./TradePost";
import { setTradeInviteHandler, tradeInviteHandler, tradeInviteOpts, tradeIdFromLocation, tradeIdOf, proposeTrade } from "./trading";
import RoomOverlay from "./RoomOverlay";
import HowToPlay from "./Tutorial";
import Debugger from "./Debugger";
import { buildNPCPlayerData, runNPCTurn, NPC_IDX, makeNpcReactor, npcWatchHandCap } from "./NPC";
// ── Wave 2 (July 2026): the NPC's reactive brain rides the
// ResponseBridge's reactor lane — registered when an NPC match
// starts, unregistered on every exit path. While registered,
// Shatterfield declares the PLAYER's response-worthy actions and
// this brain gets its answer window (the mirror of Wave 1).
import { registerReactor, unregisterReactor } from "./ResponseBridge";
import { recordStoryWin, recordDungeonClear, openDungeonPack, COIN_REWARDS, saveLoadout, saveSet, equipSet, grantCosmetic, recordFreePlayWin, recordPvpResult, DUNGEON_SECRET_IDS } from "./progression";
import { STARTER_DECKS, getStarterDeck } from "./starterDecks";
import useTitleSync from "./useTitleSync";
import StoryCutscene, { hasScene } from "./StoryCutscene"; // the approach scene before a story duel
import { useLangBoot } from "./lang"; // M9

// ═══════════════════════════════════════════════════════════════
// ⚠ MOVED BELOW THE IMPORTS, 2026-08-21 — IT WAS FAILING THE BUILD
// ═══════════════════════════════════════════════════════════════
// This helper was sitting in the MIDDLE of the import block, twenty
// lines of imports above the end of it, which trips eslint's
// `import/first` — and CRA promotes that to a compile ERROR and puts
// a full-screen red overlay in front of the game.
//
// ⭐ AND THE CONSOLE SAID NOTHING, which is the lesson. The overlay is
// a DOM element, not a console entry; `read_console_messages` came
// back clean on every reload while the app was refusing to compile
// behind it, and the page went on rendering the last good bundle's
// boot sequence. A screenshot is the only thing that catches this.
// From here on: reading the console is not proof the build is green.
// ── EVERY SCENE IS A NEW SCENE ──────────────────────────────────────
// ⛔ Caught before it shipped, and it would have been a blank screen in
// the middle of the story. <StoryCutscene> holds its own `i` and `done`
// in state. While a scene was always followed by an unmount that state
// never mattered — but a CHAINED scene replaces the props of the SAME
// mounted instance, which still has done=true from the scene that just
// finished, and a done cutscene renders null. The second half of the
// J'ynxi confrontation would simply not have appeared.
//
// One monotonic key, stamped where scenes are built rather than where
// they are rendered, so a chain link cannot be created without one.
let __sceneSeq = 0;
const scene = (s) => (s ? { ...s, key: "sc" + (++__sceneSeq) } : null);




// ─────────────────────────────────────────────────────────────
// STORY REWARD SCREEN
// Shown after beating a story opponent
// ─────────────────────────────────────────────────────────────
// Auto-returns to Minerium after 3s — tap anywhere to return immediately
function AutoReturn({ onContinue }) {
  const [countdown, setCountdown] = useState(3);
  useEffect(() => {
    const interval = setInterval(() => setCountdown(c => c - 1), 1000);
    const timeout = setTimeout(onContinue, 3000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [onContinue]);
  return (
    <div onClick={onContinue} style={{ padding: "16px", background: "linear-gradient(135deg, #2d1060, #07071a)", border: "1px solid #7c3aed", borderRadius: 10, color: "#a78bfa", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 14, fontWeight: 900, fontStyle: "italic", boxShadow: "0 0 16px #7c3aed44", textAlign: "center" }}>
      ← Returning to Minerium ({countdown})
    </div>
  );
}

function StoryRewardScreen({ reward, onContinue }) {
  const [phase, setPhase] = useState("reward"); // reward | pack
  const [revealedPack, setRevealedPack] = useState(false);

  const { crests, opponentName, opponentDialogue, isDungeonClear, dungeonName, dungeonPack, secretsFound, secretsTotal } = reward;

  if (phase === "pack" && dungeonPack) {
    const specialPack = {
      id: "special", name: "Dungeon Pack", subtitle: "Special Drop",
      icon: "◈", gradient: `linear-gradient(135deg, #1a0a2e, #0a0014)`,
      accentColor: "#d4a84b", cost: 0,
      flavor: "◆ Claimed from the depths of the Minerium. ◆",
    };
    return (
      <BoosterPack
        pack={specialPack}
        cards={dungeonPack}
        playerName={reward.storyUid}
        onClose={onContinue}
        onOpenAnother={onContinue}
      />
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,5,0.97)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, fontFamily: "Georgia, serif" }}>
      <div style={{ width: "min(92vw, 440px)", display: "flex", flexDirection: "column", gap: 16, padding: "0 4px" }}>

        {/* Victory header */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#d4a84b", letterSpacing: 6, marginBottom: 12 }}>◆ VICTORY ◆</div>
          <div style={{ fontSize: 28, fontWeight: 900, fontStyle: "italic", color: "#d4a84b", marginBottom: 6, textShadow: "0 0 24px #d4a84b88" }}>
            {opponentName} Defeated
          </div>
        </div>

        {/* Opponent post-battle dialogue */}
        <div style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(0,0,10,0.9))", border: "1px solid #7c3aed44", borderRadius: 14, padding: "18px 20px" }}>
          <div style={{ fontSize: 13, color: "#a8b4d4", fontStyle: "italic", lineHeight: 1.9 }}>
            {opponentDialogue}
          </div>
        </div>

        {/* Crest reward */}
        <div style={{ background: "linear-gradient(135deg, rgba(212,168,75,0.12), rgba(0,0,10,0.9))", border: "1px solid #d4a84b66", borderRadius: 14, padding: "18px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#4a5280", letterSpacing: 3, marginBottom: 10 }}>DIVINE CRESTS EARNED</div>
          <div style={{ fontSize: 52, fontWeight: 900, color: "#d4a84b", fontFamily: "Georgia, serif", textShadow: "0 0 28px #d4a84b" }}>
            +{crests}
          </div>
          <div style={{ fontSize: 11, color: "#4a5280", letterSpacing: 2, marginTop: 6 }}>◆</div>
        </div>

        {/* Dungeon clear bonus */}
        {isDungeonClear && (
          <div style={{ background: "linear-gradient(135deg, rgba(22,163,74,0.15), rgba(0,0,10,0.9))", border: "1px solid #16a34a66", borderRadius: 14, padding: "16px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 900, letterSpacing: 3, marginBottom: 6 }}>✓ DUNGEON CLEARED</div>
            <div style={{ fontSize: 13, color: "#a8b4d4", fontStyle: "italic", lineHeight: 1.8 }}>
              {dungeonName} has fallen. A Special Dungeon Pack awaits — with a guaranteed Holographic card of your starter deck's mascot.
            </div>
            {secretsTotal > 0 && (
              <div style={{ fontSize: 12, marginTop: 10, letterSpacing: 1, color: secretsFound >= secretsTotal ? "#16a34a" : "#b9a8ff" }}>
                ✶ Secrets uncovered: {secretsFound}/{secretsTotal}
                {secretsFound < secretsTotal && (
                  <span style={{ color: "#4a5280" }}> — the dungeon still hides what you haven't found.</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {isDungeonClear && dungeonPack ? (
          <>
            <button onClick={() => setPhase("pack")} style={{ padding: "16px", background: "linear-gradient(135deg, #2d1060, #07071a)", border: "1px solid #d4a84b", borderRadius: 10, color: "#d4a84b", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 15, fontWeight: 900, fontStyle: "italic", boxShadow: "0 0 20px #d4a84b44", transition: "all 0.2s" }}>
              ◈ Open Dungeon Pack
            </button>
            <button onClick={onContinue} style={{ padding: "12px", background: "transparent", border: "1px solid #13132e", borderRadius: 10, color: "#4a5280", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 13 }}>
              ← Return to Minerium
            </button>
          </>
        ) : (
          <AutoReturn onContinue={onContinue} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PLAYER DECK RESOLVER — one helper, two callers
// ─────────────────────────────────────────────────────────────
// The dungeon's boss-launch already did this inline; the World Map
// needs the SAME deck twice (once to derive which secret routes are
// currently visible, once to actually launch the duel), and two
// copies of a resolution order is exactly how they drift. Lifted to
// one function rather than pasted a third time.
//   loadout → the starter deck for this profile → first saved deck
// ⭐ THE LAST TERM IS THE ONE THAT WAS MISSING, AND IT WAS STORY MODE'S
// FRONT DOOR.
//
// The chain read loadout → saved-by-starterDeckId → first-saved, and
// stopped. But Onboarding.finalize writes ONLY gender, starterDeckId,
// hasCompletedOnboarding and the collection — it never writes `loadout`
// (only saveLoadout/saveSet do, and those are called from the Forge)
// and never writes the `terror_tcg_grimoires` localStorage key (only
// the Deck Builder does). So for a brand-new account every term
// returned null and this function returned null.
//
// Plaza's own resolveDeck has a fourth step — `getStarterDeck(
// profile.starterDeckId)` — which is why Free Play and the Depths
// worked while STORY MODE WAS A WALL: two resolvers, one of them a
// step short, and the short one guards the mode a new player is most
// likely to press first. Worse, the Plaza gate handler treats a null
// deck as "skip the doorman", so the bounce was not even consistent.
//
// The starter deck is not a fallback; it is what the player CHOSE in
// the intro. It belongs in the chain, last, where "I have not opened
// the Forge yet" resolves to "then play what you picked".
function resolvePlayerDeck(profile) {
  const STORAGE_KEY = "terror_tcg_grimoires";
  const starter = profile?.starterDeckId ? getStarterDeck(profile.starterDeckId) : null;
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return profile?.loadout
      || (profile?.starterDeckId ? saved[profile.starterDeckId] : null)
      || Object.values(saved)[0]
      || starter
      || null;
  } catch (e) {
    return profile?.loadout || starter || null;
  }
}

// THE ARRIVAL — the query string as it was when the module loaded, read
// once, before any effect gets to tidy the address bar. Two effects in
// App read it: the Pierre handoff (`?uid=`) and the invite landing
// (`?match=`). Each strips the URL after reading; only a snapshot lets
// the second one still see what the first one erased.
const ARRIVAL = (() => {
  try { return new URLSearchParams(window.location.search); }
  catch (_) { return new URLSearchParams(""); }
})();

export default function App() {
  // Module-level vars survive App remounts (StrictMode double-mount)
  const gameDataRef = useRef(null);
  const [screen, setScreen] = useState("menu");

  // ♪ THE SOUNDTRACK. A hook and not an element, because the returns
  // below are a fifteen-branch ladder and an element would have to be
  // pasted into every one of them. See Soundtrack.js — it is silent
  // and unbothered until the three bgm_*.mp3 files land on the CDN.
  useSoundtrack(screen);
  // ♪ The little music icon (back / pause / next / mute). Mounts itself once
  // on document.body — see MusicPlayer.js for why it is not an element here.
  useEffect(() => { mountMusicPlayer(); }, []);

  // ── H9 · THE TITLES PIPELINE (overseer mount) ─────────────────
  // One listener for the whole app. A family completing anywhere in
  // the Empire mints its title here; it never auto-equips. See
  // useTitleSync.js for why this is not a line in the vendored kit.
  useTitleSync();

  // ── M9 · THE LANGUAGE SCAFFOLD (overseer mount) ───────────────
  // Loads the saved language the moment the player is known. One
  // language exists today; the seam is live so the second one is a
  // table, not a session.
  useLangBoot();

  const [prevScreen, setPrevScreen] = useState("menu"); // always tracks the screen we came from

  // Navigate to a new screen, remembering where we came from
  // ── THE CODEX DOOR CHOICE (caP'n, 2026-09-02) — clicking the
  // Codex in Willie's Workshop now asks: The Codex (canon) or The
  // Fauxdex (experimental hypothesis, view-only)?
  const [codexChoice, setCodexChoice] = useState(false);

  const goTo = (next) => {
    setPrevScreen(screen);
    setScreen(next);
  };
  // Go back to previous screen (also resets prevScreen so double-back doesn't loop)
  const goBack = () => {
    setScreen(prevScreen);
    setPrevScreen("plaza"); // safe fallback if goBack called again
  };

  // ── Pierre handoff — accept incoming ?uid= ───────────────────
  // When Pierre (or any IE app) hands the player off to Terror with
  // `?uid=X` in the URL, skip the main menu and land on Plaza. Plaza's
  // existing onAuthStateChanged reads the persisted Firebase Auth
  // session (per-domain, browserLocalPersistence) and loads the same
  // profile — so the player arrives already logged in.
  //
  // If the player has NEVER logged into Terror on this browser, Plaza
  // will show its normal LoginOverlay and they sign in once. Subsequent
  // round-trips are seamless.
  //
  // URL is cleaned immediately so a refresh doesn't re-trigger.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incomingUid = params.get("uid");
    if (!incomingUid) return;
    // ⚠ READ THE OTHER PARAMS BEFORE YOU WIPE THEM (2026-09-06). This
    // effect is declared first, so it ran first — and it replaced the
    // whole query string with the bare pathname. The invite effect
    // below then read `?match=` off an already-empty URL. Every cross-
    // title JOIN travels through go()/A.travel, which ALWAYS appends
    // `uid`, so every friend arriving from another title lost their
    // seat code at the door and landed on a kiosk-less Plaza. ARRIVAL
    // (module scope) is the snapshot both effects read now.
    try {
      window.history.replaceState({}, "", window.location.pathname);
    } catch (_) { /* non-blocking */ }
    console.log("[IE handoff] incoming uid:", incomingUid, "— skipping to Plaza");
    setScreen("plaza");
  }, []);

  // ── THE RETURN, and the invite landing (2026-09-05) ─────────────
  // Two arrivals, one door, because they are the same question asked
  // twice: "is there a Solrend waiting for this player right now?"
  //
  //   · `?match=CODE` in the URL — a friend's invite, arriving from
  //     another IE title or a fresh tab. Held until the Plaza has a
  //     profile and a deck, then joined for real.
  //   · a localStorage breadcrumb — this player was IN a duel and the
  //     page reloaded (refresh, crash, phone locking, F5). Before
  //     today that was unrecoverable: the seat was still in Firebase
  //     with their uid on it and the kiosk answered "Match already in
  //     progress." The breadcrumb is only a HINT — `findMySeat`
  //     re-verifies against the live match, so a stale or hand-edited
  //     breadcrumb cannot seat anyone anywhere.
  const [pendingInviteCode, setPendingInviteCode] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const params = ARRIVAL;   // snapshot — see the handoff effect above
    const inviteCode = (params.get("match") || "").replace(/[^0-9]/g, "").slice(0, 4);
    if (inviteCode) {
      try { window.history.replaceState({}, "", window.location.pathname); } catch (_) {}
      setScreen("plaza");
    }
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled || !user) return;
      try { unsub(); } catch (_) {}
      if (inviteCode) { setPendingInviteCode(inviteCode); return; }
      let bc = null;
      try { bc = JSON.parse(localStorage.getItem("terror_live_match") || "null"); } catch (e) {}
      if (!bc || !bc.code) return;
      const STALE = 2 * 60 * 60 * 1000;   // ⟵ DIAL — a duel older than this is not "in progress"
      const drop = () => { try { localStorage.removeItem("terror_live_match"); } catch (e) {} };
      if (Date.now() - (bc.ts || 0) > STALE) { drop(); return; }
      try {
        const { findMySeat } = await import("./gameManager");
        const mine = await findMySeat(bc.code);
        if (cancelled) return;
        if (!mine || mine.finished) { drop(); return; }
        console.log("[THE RETURN] resuming seat", mine.seatIdx, "in match", bc.code);
        handleEnterGame(bc.code, mine.seatIdx, bc.playerName || "Aenarchist");
      } catch (e) { /* offline — the kiosk's code box is still the way back */ }
    });
    return () => { cancelled = true; try { unsub(); } catch (_) {} };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── ANSWERING A FRIEND'S JOIN IN PLACE ─────────────────────────
  // The friends layer is a sibling of <App/>, so its JOIN button cannot
  // reach a match on its own — it registers here instead. Two reasons
  // this beats letting it navigate:
  //   · `url` is domain-allowlisted to the production pages.dev, so on
  //     localhost travelling throws the player off their own build. Every
  //     dev session and every live-QA lap is that case.
  //   · Reloading a React app to reach a screen it is already rendering
  //     is a page load spent to change one piece of state.
  //
  // ⚠ AND IT NEVER YANKS ANYONE OUT OF A LIVE DUEL. If a friend's invite
  // lands mid-Solrend it is HELD, not obeyed — the kiosk opens with their
  // code already in it the moment the player next stands in the Plaza.
  // Answering it immediately would abandon a duel in progress, which is
  // the one thing an invite must never be able to do to you.
  const screenRef = useRef(screen);
  useEffect(() => { screenRef.current = screen; }, [screen]);

  // ── THE EXCHANGE POST's door (2026-09-06) ───────────────────────
  // A trade invite arriving from the friends panel opens the post in
  // place with that trade on the table — same shape as the match
  // invite handler below, same reason (never navigate off localhost,
  // never yank anyone out of a live duel: held until the Plaza).
  const [tradeProfile, setTradeProfile] = useState(null);
  const [pendingTradeId, setPendingTradeId] = useState(() => tradeIdFromLocation());
  const socialBridge = useSocialBridge();
  // The panel's TRADE verb and first look at a tapped invite — both
  // registered here so the vendored friends layer stays title-agnostic.
  const socialBridgeRef = useRef(null);
  useEffect(() => { socialBridgeRef.current = socialBridge; }, [socialBridge]);
  useEffect(() => {
    setFriendActions((f) => (f && f.status === "accepted" ? [{
      label: "Trade",
      run: async (friend) => {
        const b = socialBridgeRef.current;
        if (!b?.uid) return;
        const id = await proposeTrade(b.uid, b.myName || "Aenarchist", friend.uid, friend.name);
        if (!id) return;
        try { await b.invite(friend.uid, tradeInviteOpts(id)); } catch (e) {}
        const h = tradeInviteHandler();
        if (h) h(id, null);
      },
    }] : []));
    setInviteOpener((inv) => {
      const id = tradeIdOf(inv);
      if (!id) return false;
      const h = tradeInviteHandler();
      if (h) h(id, inv);
      return true;
    });
    return () => { setFriendActions(null); setInviteOpener(null); };
  }, []);
  useEffect(() => {
    setTradeInviteHandler((id) => {
      if (!id) return;
      setPendingTradeId(String(id));
      if (screenRef.current === "game") return;   // held — Plaza's effect opens it on return
      if (screenRef.current === "tradepost") return; // already in the post: initialTradeId updates
      setScreen("plaza");                            // Plaza's [pendingTradeId] effect walks into the post
    });
    return () => setTradeInviteHandler(null);
  }, []);

  useEffect(() => {
    setMatchInviteHandler((mc) => {
      const clean = String(mc || "").replace(/[^0-9]/g, "").slice(0, 4);
      if (!clean) return;
      setPendingInviteCode(clean);
      if (screenRef.current !== "game") setScreen("plaza");
    });
    return () => setMatchInviteHandler(null);
  }, []);
  const [gameData, setGameData] = useState(null);
  const [storyReward, setStoryReward] = useState(null);
  const [depthResult, setDepthResult] = useState(null);
  // ── H29 — the gauntlet bout outcome carried home ──────────────
  // Deliberately NOT folded into depthResult. A bout produces two
  // independent facts — what it did to your ELO, and what it did to
  // your run — and they have different lifetimes: the ELO toast is
  // per match, the run outcome is per totem. One state holding both
  // would mean a rung clear could be cleared off screen by the next
  // match's ELO toast, which is the one message in the mode nobody
  // should ever miss.
  const [gauntletResult, setGauntletResult] = useState(null);
  // ── C8 (July 17 2026): PvP result carried home to the Plaza toast ──
  const [pvpResult, setPvpResult] = useState(null);
  const [gameCollection, setGameCollection] = useState({});
  // ── COLLECTION → SHATTERFIELD (provenance + holo, race-proof) ──────
  // Two things want to fill gameCollection and they used to fight:
  //   (a) synthetic holo-collections from the launch path
  //       (buildHoloCollectionFromDeck / storyMeta.collection) — carry
  //       holoTiers for the board's foils, but only whatever provenance
  //       the caller happened to have loaded.
  //   (b) this effect — the authoritative full-profile fetch.
  // When (a)'s setGameCollection ran AFTER this effect on the same
  // render, it clobbered the fetched provenance and the Shatterfield
  // origin strip read null (the "strip not appearing" bug).
  // Fix: don't overwrite — MERGE the fetched sources/sourceTiers INTO
  // whatever's already there, per card. Order-independent: foils from
  // the synthetic set are preserved, provenance from the true profile
  // is restored. auth.uid is the only correct key (Profile-Key Seal);
  // covers free play, story, dungeon, NPC, PvP. Opponent cards still
  // get no collection, by design.
  useEffect(() => {
    if (screen !== "game") return;
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    let dead = false;
    get(ref(db, `profiles/${uid}/collection`))
      .then(snap => {
        if (dead) return;
        const full = snap.val() || {};
        setGameCollection(prev => {
          const base = prev && Object.keys(prev).length ? prev : {};
          const merged = { ...base };
          for (const [cardId, rec] of Object.entries(full)) {
            merged[cardId] = {
              ...(base[cardId] || {}),          // keep synthetic holoTier/holoCount
              ...rec,                            // bring count + provenance from profile
              ...(base[cardId]?.holoTier ? { holoTier: base[cardId].holoTier } : {}),
              ...(base[cardId]?.holoTiers ? { holoTiers: base[cardId].holoTiers } : {}),
              sources: rec.sources || base[cardId]?.sources,
              sourceTiers: rec.sourceTiers || base[cardId]?.sourceTiers,
            };
          }
          return merged;
        });
      })
      .catch(() => { /* non-fatal — board just renders without foils/sigils */ });
    return () => { dead = true; };
  }, [screen]);
  const [dungeonProfile, setDungeonProfile] = useState(null);
  // ── THE UNSTABLE WORLD (true story mode) ───────────────────
  // worldProfile mirrors dungeonProfile: the profile snapshot the
  // map screen was opened with. worldResult is the ribbon handed
  // back after a node's duel — the stamp already happened in
  // worldData.recordNodeClear; this is only what to SAY about it.
  const [worldProfile, setWorldProfile] = useState(null);
  const [worldResult, setWorldResult]   = useState(null);
  const [collectionProfile, setCollectionProfile] = useState(null);
  const [shopProfile, setShopProfile] = useState(null);
  // ── THE MAP SCENE ─────────────────────────────────────────────
  // One slot, two jobs, and the difference is whether it holds a `run`:
  //   APPROACH  — words before the duel. Carries the whole launch as a
  //               thunk, so the cutscene owns nothing about how a duel
  //               begins and fires it when the talking stops.
  //   AFTERMATH — words after it. No `run`; it just closes.
  // Story opponents have carried approach / onWin / onLoss since July
  // and the World Map played NONE of them — you walked into a boss and
  // the duel started, then it ended and you were on the map again. All
  // three were written and nobody was saying any of them.
  const [mapScene, setMapScene] = useState(null);
  const [forgeProfile, setForgeProfile] = useState(null);
  const [workshopProfile, setWorkshopProfile] = useState(null);
  const [libraryProfile, setLibraryProfile] = useState(null);
  const [factionProfile, setFactionProfile]   = useState(null);
  const [pierreProfile, setPierreProfile]     = useState(null);
  // C5 The Treatment (2026-08-18): the docs rooms — How to Play, the
  // Rulebook, and the free-play Deck Builder — now carry a profile so
  // their RoomOverlay wrap shows the player's own equipped backdrop +
  // menu/chat like every other treated room. Plaza passes it through.
  const [docsProfile, setDocsProfile] = useState(null);

  // NPC engine refs — no useEffect needed, listener attached directly in handleEnterNPC
  const npcUnsubRef = useRef(null);
  const npcRunning = useRef(false);
  const npcLastTurn = useRef(-1);
  const npcDifficultyRef = useRef(null);
  const npcStarting = useRef(false); // prevents double-fire from React StrictMode

  // ── Enter NPC Match ──────────────────────────────────────────
  const handleEnterNPC = async (playerName, deckData, prophecy, hand, difficulty, storyMeta = null) => {
    // Prevent double-fire (React StrictMode calls effects twice in dev)
    if (npcStarting.current) { console.log("[NPC] double-fire prevented"); return; }
    npcStarting.current = true;
    try {
      // Clean up any previous NPC listener (and any stale reactor)
      if (npcUnsubRef.current) {
        npcUnsubRef.current();
        npcUnsubRef.current = null;
      }
      unregisterReactor();
      npcRunning.current = false;
      npcLastTurn.current = -1;
      npcDifficultyRef.current = difficulty;

      // ═══════════════════════════════════════════════════════
      // ⭐ THE ADVERSARY'S DECK — authored first, random second
      // ═══════════════════════════════════════════════════════
      // This line was `STARTER_DECKS[Math.floor(Math.random() * …)]`
      // and NOTHING ELSE, in every mode, for every opponent, since the
      // launcher was written. Which meant every `starterDeckId` in the
      // game was DECORATION: the eight Inner Emperium villains each
      // named a deck and none of them ever got it, the World Map's
      // bosses named decks and never got them, and Kel'dara played
      // whatever fell out of the bag.
      //
      // caP'n's 2026-08-21 Ilk law makes that unacceptable rather than
      // merely untidy: *"Zones A and F will focus on Dragon and Time
      // decks… Zone Z will use all current 4 as well, just give final
      // boss Ant deck."* A per-zone Ilk law is a lie if the deck is a
      // dice roll, so the roll is now the FALLBACK and the authored id
      // is the answer.
      //
      // ⚠ AND IT VALIDATES RATHER THAN TRUSTS. A typo'd id would
      // otherwise hand `buildNPCPlayerData` an undefined deck and open
      // the duel with an empty adversary — a silent, unattributable
      // bug. A miss warns loudly and falls back to random, so a bad
      // row costs one console line instead of one unplayable node.
      let npcDeck = null;
      if (storyMeta?.npcDeckId) {
        npcDeck = STARTER_DECKS.find(d => d.id === storyMeta.npcDeckId) || null;
        if (!npcDeck) console.warn("[NPC deck] no such starter deck:", storyMeta.npcDeckId, "— falling back to random");
      }
      if (!npcDeck) npcDeck = STARTER_DECKS[Math.floor(Math.random() * STARTER_DECKS.length)];
      // THE VANGUARD (2026-08-17). Only the World Map sends a tier, so
      // every other launcher — Plaza ranked, free play, Dungeon bosses,
      // the Gauntlet, the Depths — gets 0 and an empty opponent board,
      // exactly as before. Story Mode is the one place an opponent is
      // allowed to already be holding the table.
      const npcPlayerData = buildNPCPlayerData(difficulty, npcDeck, storyMeta?.vanguardTier || 0);

      // Deal starting hand of 5 cards
      // Draw starting hand AND deplete grimoire — hand + grimoire = full deck
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deckData.grimoire || {}, [], 5);
      deckData = { ...deckData, grimoire: depletedGrimoire };
      console.log("[handleEnterNPC] starting hand:", startingHand, "grimoire remaining:", Object.keys(depletedGrimoire).length);

      // Create match (player 0 = human)
      const code = await createMatch(
        playerName,
        deckData.grimoire,
        deckData.luxmoire,
        prophecy,
        startingHand,
        deckData.curios || [null, null],
        deckData.esclings || null,
      );

      // Inject NPC as player 1
      await updatePlayer(code, NPC_IDX, npcPlayerData);

      // ── THE SECOND WIND ──────────────────────────────────────
      // A multi-phase boss's phases ride the MATCH, not component
      // state: they have to survive a reconnect mid-boss, and in PvP
      // both seats must agree which phase they are in. Written before
      // `status: "active"` for the same reason the Gauntlet's carried
      // Anima is — the board mounts on that flip, and a boss whose
      // phases arrive a frame late is a boss that can be ended early.
      if (Array.isArray(storyMeta?.bossPhases) && storyMeta.bossPhases.length) {
        await updateMatch(code, { bossPhases: storyMeta.bossPhases, bossSeat: NPC_IDX, phase: 0 });
        console.log("[secondWind] boss carries", storyMeta.bossPhases.length, "extra phase(s)");
      }

      // ── H29 — THE CARRIED ANIMA ─────────────────────────────
      // The one mechanic V'yen's Gauntlet is built on: within a rung,
      // you walk into every bout after the first on whatever Anima
      // the last one left you.
      //
      // It lands HERE, between creating the match and setting it
      // active, and both halves of that placement matter. After
      // createMatch, because seat 0 has to exist before it can be
      // written. Before `status: "active"`, because the board mounts
      // on that flip — seed it a frame later and the player watches
      // their Anima jump from 30 down to 11 on the opening frame,
      // which reads as damage they did not take.
      //
      // `createMatch` hardcodes `anima: 30` and is left alone. Adding
      // a parameter would put the number on every call site in the
      // game to serve one mode; a targeted write costs one line and
      // nothing outside the gauntlet ever notices.
      //
      // Read from the run document rather than from meta. Plaza is
      // what builds the depths meta and Plaza knows nothing about
      // runs — and keeping it that way is why not one line of that
      // 4,395-line file had to change for any of this.
      if (storyMeta?.isEndlessDepths && storyMeta?.storyUid) {
        try {
          const { loadRun, animaForBout, START_ANIMA } = await import("./gauntlet");
          const run = await loadRun(storyMeta.storyUid);
          if (run) {
            const seed = animaForBout(run);
            if (seed !== START_ANIMA) {
              await updatePlayer(code, 0, { anima: seed });
              console.log("[Gauntlet] carried Anima seeded:", seed, "bout", run.bout + 1, "of", run.totem.length);
            }
          }
        } catch (e) {
          // Fail OPEN. A gauntlet bout that opens at the standard 30
          // is a slightly easier bout; a gauntlet bout that refuses
          // to start is a broken mode. The former is recoverable by
          // the player, the latter is not.
          console.warn("[Gauntlet] Anima carry failed — opening at default:", e);
        }
      }

      // Set match to active
      await updateMatch(code, { status: "active" });

      console.log("[NPC] Match created:", code, "difficulty:", difficulty);

      // ── Attach NPC listener DIRECTLY here, before screen change ──
      // This guarantees the listener is live before any turns fire
      npcUnsubRef.current = listenToMatch(code, async (match) => {
        console.log("[NPC listener] fired — status:", match?.status, "activePlayer:", match?.activePlayer, "turn:", match?.turn);

        if (!match || match.status === "finished") return;
        // R10 — the brain's hand-cap watcher (the twin of the board's):
        // a ninth card the PLAYER's Tome landed on the brain is settled
        // now, on the player's half-turn, not carried into the brain's.
        if (match.activePlayer !== NPC_IDX) { npcWatchHandCap(code, match).catch(() => {}); return; }

        const turnKey = match.turn ?? 0;
        console.log("[NPC listener] NPC turn! turnKey:", turnKey, "lastTurn:", npcLastTurn.current);

        if (npcLastTurn.current === turnKey) {
          console.log("[NPC listener] already handled turn", turnKey, "skipping");
          return;
        }
        if (npcRunning.current) {
          console.log("[NPC listener] already running, skipping");
          return;
        }

        const isFirstTurnOfMatch = npcLastTurn.current === -1;
        npcLastTurn.current = turnKey;
        npcRunning.current = true;

        try {
          // ── Bug E gate: if NPC won the coin flip, the result overlay holds
          // for ~3500ms (Shatterfield CoinFlipResult). Block the first NPC turn
          // until that overlay has cleared, otherwise the player walks into a
          // board where the NPC has already played its opening.
          if (isFirstTurnOfMatch) {
            console.log("[NPC listener] first turn of match — waiting 4000ms for coin flip overlay to dismiss");
            await new Promise(r => setTimeout(r, 4000));
          }
          await runNPCTurn(code, match, npcDifficultyRef.current);
        } catch (e) {
          console.error("[NPC] turn error:", e);
        }

        npcRunning.current = false;
      });

      // ── Wave 2: arm the reactive brain alongside the turn listener ──
      // Same lifecycle, same cleanup points. If registration is ever
      // skipped, requestReaction resolves {responded:false} instantly —
      // the player's actions simply flow free (fail-open by design).
      registerReactor(makeNpcReactor(code, difficulty));

      console.log("[NPC] listener attached, entering Shatterfield");

      const gd = { code, playerIdx: 0, playerName, isNPC: true, storyMeta: storyMeta || null };
      gameDataRef.current = gd;
      setGameData(gd);
      setGameCollection(storyMeta?.collection || {});
      setScreen("game");
    } catch (e) {
      console.error("NPC match start failed:", e);
      npcStarting.current = false; // only reset on error — success resets on exit
    }
  };

  // ── Enter Human Match ────────────────────────────────────────
  const handleEnterGame = (code, playerIdx, playerName) => {
    // Clean up NPC listener if switching to human match
    if (npcUnsubRef.current) {
      npcUnsubRef.current();
      npcUnsubRef.current = null;
    }
    unregisterReactor(); // Wave 2 — no NPC brain answers a human duel
    // ⭐ THE REF, NOT ONLY THE STATE. handleExit reads gameDataRef —
    // never gameData — because by the time a duel exits the component
    // has re-rendered and a stale closure would hand back the wrong
    // match. handleEnterNPC sets BOTH. This path, the ONLY entry point
    // for a human-vs-human Solrend, set only the state.
    //
    // So at PvP exit `gd` was null and the entire C8/C16 block was
    // skipped: no crests, no pvp wins or losses, no ladder score, no
    // peak, no totalWins, no leaderboard mirror, no terror:first_pvp,
    // no terror:pvp_10, and no homecoming toast. Every human-vs-human
    // duel ever played recorded absolutely nothing. The concede
    // penalty had nothing to apply itself to either.
    const gdPvp = { code, playerIdx, playerName };
    gameDataRef.current = gdPvp;
    setGameData(gdPvp);
    setScreen("game");

    // ⭐ THE BOSS GETS A BRAIN (2026-09-06). Gauntlet — Duo and Story
    // Co-op seat an AI at NPC_IDX, but this door — the only one a kiosk
    // match enters through — never attached a listener, so nobody ever
    // played its Turn: the grace-ender force-ended every boss Turn and
    // two humans "won" against a statue. The HOST's client (seat 0)
    // drives the brain, exactly as handleEnterNPC does for 1v1; the
    // partner's client stays a pure renderer, so one brain acts.
    // Read once, off the live match: the mode and the brief are data
    // on the node, not something threaded through six components.
    (async () => {
      try {
        const { getMatch } = await import("./gameManager");
        const m = await getMatch(code);
        if (!m || gameDataRef.current?.code !== code) return;
        const aiSeat = m.players?.[NPC_IDX];
        const hasBrainSeat = aiSeat && aiSeat.controller === "ai";
        const coopMode = ["gauntlet2v2", "story-coop"].includes(m.mode) ? m.mode : null;
        // The exit ledger needs the mode on BOTH clients.
        gameDataRef.current = { ...gameDataRef.current, coopMode, aiMeta: m.aiMeta || null, hasBrainSeat: !!hasBrainSeat };
        if (!hasBrainSeat || playerIdx !== 0) return;
        const difficulty = m.aiMeta?.difficulty || "Champion";
        npcDifficultyRef.current = difficulty;
        npcRunning.current = false;
        npcLastTurn.current = -1;
        npcUnsubRef.current = listenToMatch(code, async (match) => {
          if (!match || match.status === "finished") return;
          if (match.activePlayer !== NPC_IDX) { npcWatchHandCap(code, match).catch(() => {}); return; }   // R10 watcher — same as 1v1
          const turnKey = match.turn ?? 0;
          if (npcLastTurn.current === turnKey) return;
          if (npcRunning.current) return;
          const isFirstTurnOfMatch = npcLastTurn.current === -1;
          npcLastTurn.current = turnKey;
          npcRunning.current = true;
          try {
            if (isFirstTurnOfMatch) await new Promise(r => setTimeout(r, 4000));   // coin-flip overlay — same wait as 1v1
            await runNPCTurn(code, match, npcDifficultyRef.current);
          } catch (e) { console.error("[NPC co-op] turn error:", e); }
          npcRunning.current = false;
        });
        registerReactor(makeNpcReactor(code, difficulty));
        console.log("[NPC co-op] brain attached at seat", NPC_IDX, "difficulty", difficulty, "mode", coopMode);
      } catch (e) { console.warn("[NPC co-op] could not attach the brain:", e); }
    })();
    // ⭐ THE BREADCRUMB (2026-09-05). Seat identity used to live only in
    // React state, so a refresh mid-duel lost which chair was yours and the
    // kiosk answered "Match already in progress." Two values in
    // localStorage are the whole fix — the seat itself is re-verified
    // against Firebase by findMySeat before anything is resumed, so a
    // stale or forged breadcrumb can never seat anybody anywhere.
    try {
      localStorage.setItem("terror_live_match", JSON.stringify({ code, playerIdx, playerName, ts: Date.now() }));
    } catch (e) {}
  };

  const handleExit = async (winnerId, exitOpts = {}) => {
    // Clean up NPC listener on exit
    if (npcUnsubRef.current) {
      npcUnsubRef.current();
      npcUnsubRef.current = null;
    }
    unregisterReactor(); // Wave 2 — the reactive brain sleeps with the match
    npcRunning.current = false;
    npcLastTurn.current = -1;
    npcDifficultyRef.current = null;
    npcStarting.current = false; // allow next match to start

    // ── Resolve meta FIRST — used by both branches below ─────
    const meta = gameDataRef.current?.storyMeta;
    // ── R98 (2026-08-25) — `winnerId === 0` IS NO LONGER THE WHOLE
    // QUESTION. A Solrend can end with more than one winner, so the
    // human seat can WIN a duel whose PRIMARY winner is somebody else
    // (their Prophecy came true on the adversary's finishing blow).
    // Shatterfield hands back `exitOpts.iWon`, computed through
    // didSeatWin, which reads the `winners` array as well as `winner`
    // and already knows how to read a seat OR a team id. It is
    // preferred whenever it is present; `winnerId === 0` stays as the
    // fallback for the one caller that passes no options at all.
    const humanWon = typeof exitOpts?.iWon === "boolean" ? exitOpts.iWon : winnerId === 0;

    // ── Endless Depths result flow ────────────────────────────
    if (meta?.isEndlessDepths && meta?.storyUid) {
      // ⭐ ABANDONING A BOUT IS A LOSS, NOT A DRAW.
      //
      // Nothing in Shatterfield can finish a duel with a null winner —
      // endMatch always takes a winnerIdx — so `winnerId === null`
      // reaches here from exactly one place: handleExitClick's NPC
      // branch, `onExit(null, {})`, which is ONE UNCONFIRMED TAP on the
      // exit glyph in any NPC duel.
      //
      // Scored as a draw that was worth −5 ELO flat instead of the −14
      // to −23 an honest loss costs, AND the `result !== "loss"` gate
      // below then paid the draw's coin reward. So a player reading a
      // losing board could tap once, keep four fifths of the ELO, and
      // be paid for it — and that ELO is what gates which Gauntlet
      // rungs unlock. It is not an exploit somebody has to discover;
      // it is the cheapest button on the screen.
      //
      // A DRAW REMAINS EXPRESSIBLE. DEPTH_ELO_CONFIG.DRAW stays, and
      // the day the engine can genuinely end a duel level — a mutual
      // anima-zero, a turn cap — it will pass a real signal rather than
      // an absent one. What is not allowed is "absent" meaning "level".
      const result = winnerId === null ? "loss" : humanWon ? "win" : "loss";
      try {
        const { loadDepthProfile } = await import("./EndlessDepths");
        const depthProfile = await loadDepthProfile(meta.storyUid);
        const currentElo   = depthProfile.elo || 0;
        const eloChange    = calcEloChange(currentElo, meta.difficulty || "Champion", result);
        const opponent     = {
          id:            meta.depthOpponentId   || "unknown",
          name:          meta.depthOpponentName || "Unknown",
          isGatekeeper:  !!meta.depthIsGatekeeper,
          // Difficulty must ride along — V2 recomputes the Elo delta with
          // DIFF_MULT, and without this the persisted delta silently
          // diverged from the displayed calcEloChange result.
          difficulty:    meta.difficulty || "Champion",
        };
        // recordDepthResultLegacy unpacks the opponent object for the V2
        // engine. Returns { newElo, newPeak, eloChange } — only newElo is
        // consumed below (the old { newStreak } destructure was dead: V2
        // never returned it, so it was always undefined).
        const { newElo, rewards } = await recordDepthResultLegacy(
          meta.storyUid, result, opponent, eloChange
        );
        // Award DC based on rank and result
        let dcEarned = 0;
        if (result !== "loss") {
          const { earnDepthCoins } = await import("./progression");   // ◕ — the Depths always paid Coins; only the old name said otherwise
          const { rank } = getRankInfo(currentElo);
          dcEarned = await earnDepthCoins(meta.storyUid, result, rank.id, opponent.isGatekeeper);
        }
        setDepthResult({ humanWon, eloChange, newElo, opponentName: opponent.name, isGatekeeper: opponent.isGatekeeper, dcEarned, rewards: rewards || null });
      } catch (e) {
        console.error("[Endless Depths result] error:", e);
      }

      // ── H29 — THE GAUNTLET BOUT ─────────────────────────────
      // The ELO/trophy write above is unchanged and still runs for
      // every bout: a gauntlet bout is a Depths match, it just also
      // belongs to a run. This block adds the run's half of the
      // bookkeeping and NOTHING ELSE, which is why the whole mode
      // needed no new branch and no new meta field — `isEndlessDepths`
      // was already the signal, and the run itself is a document in
      // Firebase rather than something threaded through props.
      //
      // THE ANIMA IS READ OFF THE MATCH, NOT PASSED BACK. The duel's
      // exit callback is `onExit(winnerId, opts)` and it has never
      // carried a final Anima. Teaching it to would mean editing
      // Shatterfield.js — 10,000 lines, the frozen canonical board —
      // for one number that is already sitting in the database. The
      // match node is not deleted on exit, so seat 0's Anima is
      // simply still there to be read. Cheapest correct answer.
      try {
        const { recordBout } = await import("./gauntlet");
        let endAnima = 0;
        const code = gameDataRef.current?.code;
        if (code) {
          const { get: fbGet, ref: fbRef } = await import("firebase/database");
          const { db: fbDb } = await import("./firebase");
          const snap = await fbGet(fbRef(fbDb, `matches/${code}/players/0/anima`));
          if (snap.exists()) endAnima = Number(snap.val()) || 0;
        }
        const boutRes = await recordBout(meta.storyUid, { won: humanWon, endAnima });
        // "no_run" is the ordinary case for a one-off Depths match
        // launched outside a run, and it is not an error.
        if (boutRes.outcome !== "no_run") setGauntletResult(boutRes);
      } catch (e) {
        console.error("[Gauntlet bout] error:", e);
      }

      gameDataRef.current = null;
      setGameData(null);
      setScreen("plaza");
      return;
    }

    // ── THE DOOR's result ─────────────────────────────────────
    // Win and the Minerium opens — permanently, stamped once. Lose and
    // you are back at the Plaza with nothing taken; the door is not a
    // punishment, it is a door.
    if (meta?.isWorldGate && meta?.worldGateId) {
      let firstTime = false;
      if (humanWon) {
        try { firstTime = await recordGateClear(meta.storyUid, meta.worldGateId); }
        catch (e) { console.warn("[worldGate] clear write failed:", e?.message); }
      }
      // ── ⬡ WHAT THE DOOR IS WORTH ──────────────────────────
      // caP'n, 2026-09-07: *"SHV sends his Lyt avatar to find the
      // intruders — after defeating it they are cloaked by V'ael's
      // Veil."* So the cloak is not a pickup lying on the map and it
      // is not a node reward; it is what walking away from the doorman
      // gets you, and it is handed over the instant that fight ends.
      //
      // ⚠ ON EVERY WIN, NOT ONLY THE FIRST. `recordGateClear` returns
      // false for a door already answered, and the grant is a stamp of
      // its own — so a player who somehow re-fights the door still
      // ends up holding the Veil, and one who already holds it writes
      // nothing. Tying the grant to `firstTime` would mean a failed
      // gate write silently costing the tool forever.
      //
      // The grant is derived from the TOOL's own `wonFrom`, so adding
      // a second tool to a second doorman is a row in vaelsTools.js
      // and no code here.
      let toolScene = null;
      if (humanWon) {
        const tool = toolWonFromGate(meta.worldGateId);
        if (tool) {
          try { await recordToolGrant(meta.storyUid, tool.id); }
          catch (e) { console.warn("[tool] grant write failed:", tool.id, e?.message); }
          if (tool.grantScene?.lines?.length) {
            toolScene = scene({
              lines: tool.grantScene.lines,
              speaker: tool.grantScene.speaker || tool.lender || "",
              speakerTitle: tool.grantScene.speakerTitle || "",
              accent: tool.grantScene.accent || tool.accent || "#c8963c",
              playerName: STORY_PLAYER,
              run: null, then: null,
            });
          }
        }
      }

      // See the note on the INTERCEPTION's result below — same shape,
      // same fix. A loss here goes to the Plaza, where `mapScene` has
      // no stage, so the doorman's parting words were queued for a
      // screen that would never render them and then played over the
      // next map entry instead.
      const after = humanWon ? meta.bossOnWin : meta.bossOnLoss;
      if (humanWon && Array.isArray(after) && after.length) {
        setMapScene(scene({
          lines: after, speaker: meta.bossName || "", speakerTitle: meta.bossTitle || "",
          accent: meta.bossAccent || "#c8963c", run: null, playerName: STORY_PLAYER,
          then: toolScene,      // ⬡ ...and then she is standing there
        }));
      } else if (humanWon && toolScene) {
        // A doorman with no parting words still owes you the cloak.
        setMapScene(toolScene);
      } else if (!humanWon) {
        setMapScene(null);
      }
      gameDataRef.current = null;
      setGameData(null);
      if (humanWon) {
        if (meta.gateProfile) setWorldProfile(meta.gateProfile);
        setScreen("worldmap");
        if (firstTime) console.log("[worldGate]", meta.worldGateId, "opened for the first time.");
      } else {
        setScreen("plaza");
      }
      return;
    }

    // ── THE INTERCEPTION's result ─────────────────────────────
    // caP'n, 2026-08-20: *"If you lose, you get sent to plaza and
    // immediately upon re-entry it starts again."* Both halves are
    // here: a loss stamps NOTHING and drops to the Plaza, and the map
    // re-fires it on the next load because `pendingAmbush` reads a
    // stamp that was never written.
    //
    // A win stamps once, ever, and puts the player back exactly where
    // they were standing when SHV's second Avatar walked out of the
    // air at them — an ambush is an interruption, not a journey.
    if (meta?.isWorldAmbush && meta?.worldAmbushId) {
      let firstTime = false;
      if (humanWon) {
        try { firstTime = await recordAmbushClear(meta.storyUid, meta.worldAmbushId); }
        catch (e) { console.warn("[ambush] clear write failed:", e?.message); }
      }
      // ⚠ A SCENE THAT CANNOT BE SHOWN MUST NOT BE QUEUED. `mapScene`
      // renders ONLY inside the worldmap branch, and nothing clears it
      // on the way to the Plaza. Setting the loss lines here and then
      // sending the player to the Plaza meant they never saw them —
      // and then the scene AMBUSHED THE NEXT MAP ENTRY, playing a
      // conversation about a fight lost minutes ago over a fresh map,
      // and blocking the re-fire (`ambushBlocked`) until it was
      // dismissed. So the aftermath plays only on the path that has a
      // stage, and the loss path clears the boards on its way out.
      // (The DOOR's loss path above has the same shape and the same
      // fix, applied in the same pass.)
      const after = humanWon ? meta.bossOnWin : meta.bossOnLoss;
      if (humanWon && Array.isArray(after) && after.length) {
        setMapScene(scene({
          lines: after, speaker: meta.bossName || "", speakerTitle: meta.bossTitle || "",
          accent: meta.bossAccent || "#c8963c", run: null, playerName: STORY_PLAYER,
        }));
      } else if (!humanWon) {
        setMapScene(null);
      }
      gameDataRef.current = null;
      setGameData(null);
      setScreen(humanWon ? "worldmap" : "plaza");
      if (firstTime) console.log("[ambush]", meta.worldAmbushId, "answered.");
      return;
    }

    // ── THE UNSTABLE WORLD result flow ────────────────────────
    // Win or lose, you walk back out onto the map where you stood.
    // A win stamps the node (append-only, transaction-guarded, and
    // idempotent — see worldData.recordNodeClear); a loss stamps
    // nothing and costs nothing but the walk back.
    if (meta?.isWorldMap && meta?.worldNodeId) {
      if (humanWon) {
        try {
          const res = await recordNodeClear(meta.storyUid, meta.worldNodeId, {
            secret: !!meta.worldSecret,
          });
          // ⭐ `win: true` — the ribbon had no way to know.
          // recordNodeClear returns { firstClear, firstSecret, crests,
          // prize, child, shards, storySet, role } and NO `win` key, and
          // WorldMap's ResultRibbon reads `result.win ?? result.firstClear`.
          // So on a REPEAT win of an already-cleared node firstClear is
          // false, `win` is undefined, and the ribbon falls all the way
          // through to the loss string — "— Not this time" — in the loss
          // colour, with the loss border, immediately after a victory.
          // Replaying a cleared node is the map's designed loop, so this
          // is the ordinary case, not an edge one. This branch only runs
          // when humanWon, so the value is not a guess.
          if (res) setWorldResult({ ...res, win: true, nodeId: meta.worldNodeId });
        } catch (e) {
          console.error("[World Map result] error:", e);
        }
      }
      // ── THE AFTERMATH SCENE ───────────────────────────────
      // A boss reacts to how it went. Set BEFORE the screen flips so it
      // is already mounted when the map appears — set it after and the
      // player gets a frame of map before being talked at.
      const after = humanWon ? meta.bossOnWin : meta.bossOnLoss;

      // ── THE INTERLUDES ────────────────────────────────────
      // Story that is not a duel — a trace of a child, J'ynxi's coin,
      // the Champion at the end. Only on a WIN, only once ever
      // (stamped), and played after the boss has finished reacting.
      // Built into a chain here so the whole run is decided before a
      // single frame renders; a scene that has to go and ask Firebase
      // what comes next is a scene with a hole in the middle of it.
      let chain = null;
      if (humanWon && meta.storyUid) {
        try {
          const prog = await loadWorldProgress(meta.storyUid);
          const fired = new Set(Object.keys(prog.beats || {}));
          const head = interludeAfter(meta.worldNodeId, fired);
          const run = head ? interludeChain(head, fired) : [];
          for (const beat of run) {
            // Stamp BEFORE playing. A player who closes the tab mid-
            // cutscene has still had the beat; a player who gets it
            // twice has been told their child is missing twice.
            await recordBeat(meta.storyUid, beat.id);
            if (beat.grant?.coinSkin) {
              try {
                const { unlockCoinSkin } = await import("./progression");
                await unlockCoinSkin(meta.storyUid, beat.grant.coinSkin, 0, beat.grant.source || "story", "CC");
              } catch (e) {
                // A gift that fails to land must never eat the scene it
                // was given in. It is still buyable in the Emporium.
                console.warn("[story] grant failed:", beat.grant.coinSkin, e?.message);
              }
            }
          }
          // Built back-to-front so each scene already holds the next.
          chain = run.reduceRight((next, beat) => scene({
            lines: beat.lines,
            speaker: beat.speaker || "",
            speakerTitle: beat.speakerTitle || "",
            accent: beat.accent || "#c8963c",
            playerName: STORY_PLAYER,
            run: null,
            then: next,
          }), null);
        } catch (e) {
          console.warn("[story] interlude check failed:", e?.message);
        }
      }

      if (Array.isArray(after) && after.length) {
        setMapScene(scene({
          lines: after,
          speaker: meta.bossName || "",
          speakerTitle: meta.bossTitle || "",
          accent: meta.bossAccent || "#c8963c",
          playerName: STORY_PLAYER,
          run: null,          // nothing held — this one just closes
          then: chain,        // ...but it may hand on to a beat
        }));
      } else if (chain) {
        setMapScene(chain);
      }
      gameDataRef.current = null;
      setGameData(null);
      setScreen("worldmap");
      return;
    }

    // ── Story Mode reward flow ────────────────────────────────

    console.log("[handleExit] meta:", JSON.stringify(meta), "humanWon:", humanWon, "winnerId:", winnerId);
    if (meta?.storyOpponentId && meta?.storyUid) {
      // Always return to story mode after a story fight — win or loss.
      // DN-FIX: unless the fight was launched FROM the dungeon — then the
      // Aenarchist walks back into the Outer Veil where they stood.
      if (!humanWon) {
        console.log("[handleExit] loss/exit — going to", meta.returnToDungeon ? "dungeon1" : "plaza");
        gameDataRef.current = null;
        setGameData(null);
        setScreen(meta.returnToDungeon ? "dungeon1" : "plaza");
        return;
      }
      try {
        const { OPPONENTS } = await import("./storyData");
        const opponent = OPPONENTS[meta.storyOpponentId];
        const result = await recordStoryWin(meta.storyUid, meta.storyOpponentId, meta.storyDungeonId);
        const crests = result?.crests || COIN_REWARDS.NPC_WIN;   // ◕ — the alias that said "CREST" was removed 2026-08-18

        // ── Cosmetic grant: First Win Glow (aura, common) ──
        // First story win ever grants the starter aura. Append-only —
        // safe to call every time; no-op if already owned.
        if (result?.firstDefeat) {
          grantCosmetic(meta.storyUid, "first_win_glow", {
            source: "terror:first_win_ever",
            rarity: "common",
            slot:   "aura",
          }).catch(e => console.warn("[grantCosmetic] first_win_glow failed:", e));
        }

        // Check if this win clears the dungeon
        const { getPlayerProfile } = await import("./progression");
        const profile = await getPlayerProfile(meta.storyUid);
        const defeated = profile?.storyProgress?.defeatedOpponents || [];
        const dungeonOpponents = Object.values(OPPONENTS).filter(o => o.dungeonId === meta.storyDungeonId);
        const allDefeated = dungeonOpponents.every(o => defeated.includes(o.id));

        let dungeonPackCards = null;
        let dungeonClearCrests = 0;

        if (allDefeated) {
          const clearResult = await recordDungeonClear(meta.storyUid, meta.storyDungeonId);
          if (!clearResult?.alreadyCleared) {
            dungeonClearCrests = COIN_REWARDS.STORY_CHAPTER_CLEAR;   // ◕
            const starterDeckId = profile?.starterDeckId || "h01_deck";
            const packResult = await openDungeonPack(meta.storyUid, meta.storyDungeonId, starterDeckId);
            dungeonPackCards = packResult?.cards || null;
          }
        }

        // DN-FIX v2: Dungeon1 unmounts during the battle and, on remount,
        // derives defeated bosses from profile.storyProgress.defeatedOpponents
        // (Firebase — already updated by recordStoryWin above). The OLD v1
        // localStorage stamp (`terror_dungeon1_run_{name}` + solvedRooms[])
        // was a dead vestige Dungeon1 v2 never read (v2's localStorage run
        // tracks floor puzzles under a different key/shape). The REAL bug:
        // dungeonProfile was never refreshed, so the dungeon remounted with
        // a stale profile and the just-defeated boss stood back up. Fix:
        // fold the fresh storyProgress into dungeonProfile before returning.
        if (meta.returnToDungeon && profile?.storyProgress) {
          // D1 wave: fold the coin balance too — the dungeon HUD shows the
          // profile balance, and it just changed. storyProgress alone
          // left the coin display stale after every battle.
          setDungeonProfile(p =>
            p ? { ...p, storyProgress: profile.storyProgress,
                  chocolateCoins: profile.chocolateCoins ?? p.chocolateCoins,
                  divineCrests:   profile.divineCrests   ?? p.divineCrests } : p);
        }

        // Secrets progress — surfaced on the DUNGEON CLEARED panel so
        // the player immediately knows whether the dungeon is truly
        // 100% or still hiding eggs worth returning for.
        const secretRoster = DUNGEON_SECRET_IDS[meta.storyDungeonId] || [];
        const secretMap = profile?.storyProgress?.dungeonSecrets?.[`d${meta.storyDungeonId}`] || {};
        const secretsFoundCount = secretRoster.filter(id => secretMap[id]).length;

        setStoryReward({
          crests: crests + dungeonClearCrests,
          opponentName: opponent?.name || "Opponent",
          opponentDialogue: opponent?.dialogue?.onWin?.[0] || "◆ Well fought, Aenarchist.",
          isDungeonClear: allDefeated,
          dungeonName: opponent?.dungeonId ? `Dungeon ${opponent.dungeonId}` : "",
          dungeonPack: dungeonPackCards,
          secretsFound: secretsFoundCount,
          secretsTotal: secretRoster.length,
          storyUid: meta.storyUid,
          dungeonId: meta.storyDungeonId,
          returnToDungeon: !!meta.returnToDungeon,
        });
        gameDataRef.current = null;
        setGameData(null);
        setScreen("storyreward");
        return;
      } catch (e) {
        console.error("[Story reward] error:", e);
      }
    }

    // ── ◆ THE INNER EMPERIUM's result ─────────────────────────
    // ⚠ THE LADDER WAS RESTORED THIS SESSION AND LANDED IN A DEAD END.
    // `isInnerEmperium` has been passed in the launch meta for months
    // and is READ IN EXACTLY ONE PLACE — the free-play gate below,
    // which EXCLUDES it. So an Inner Emperium victory recorded nothing:
    // no coins, no totalWins, no leaderboard, no achievement, and — the
    // part that matters now — no `innerWins`. The counter that gates
    // the ladder was only ever written by the Endless Depths path,
    // which the ladder does not enter. Every rung above the first was
    // therefore unreachable by playing the ladder itself.
    //
    // It records where every other repeatable win records: through
    // recordFreePlayWin, which grants NPC_WIN coins, bumps totalWins,
    // mirrors the leaderboard and hangs the faction-war tally off it.
    // Then the villain's own `rewardCrests` is paid ONCE, ever, per
    // villain, stamped in the profile — a first-kill is a legal ◆
    // faucet by the Faucet Law and a rematch is not.
    if (humanWon && meta?.isInnerEmperium && meta?.villainId) {
      const iuid = meta.storyUid || auth?.currentUser?.uid;
      if (iuid) {
        (async () => {
          try {
            const { recordInnerWin } = await import("./progression");
            await recordInnerWin(iuid, meta.villainId);
          } catch (e) { console.warn("[innerEmperium] record failed:", e?.message); }
        })();
      }
    }

    // ── FREE PLAY win recording ───────────────────────────────
    // GATE FIX (July 3, same-day correction): the original gate was
    // `!meta`, but Plaza's free-play kiosk passes `{ collection }` (and
    // now `starterDeckId`) as storyMeta — meta is TRUTHY for kiosk free
    // play, so the record never fired. Free play is identified by the
    // ABSENCE of mode markers instead: no storyOpponentId (story/dungeon),
    // no isEndlessDepths, no isInnerEmperium. Those three paths record
    // through their own branches above. Human is always seat 0 in NPC
    // matches, so humanWon is safe here. Fire-and-forget:
    // recordFreePlayWin grants NPC_WIN crests (+first-win bonus), bumps
    // totalWins/freePlayWins, mirrors the leaderboard, grants
    // terror:first_freeplay / terror:freeplay_50 — and, when the win was
    // with a PRESET starter deck (meta.starterDeckId), stamps
    // starterWins/{deckId} toward terror:all_starters.
    const isFreePlay = gameDataRef.current?.isNPC
      && !meta?.storyOpponentId
      && !meta?.isEndlessDepths
      && !meta?.isInnerEmperium;
    if (humanWon && isFreePlay) {
      const uid = auth?.currentUser?.uid;
      if (uid) {
        recordFreePlayWin(uid, meta?.starterDeckId || null).catch(e =>
          console.warn("[recordFreePlayWin] failed:", e));
      }
    }

    // ── PvP result flow (C8, July 17 2026) ────────────────────
    // Human-vs-human Solrends finally pay. Seat-correct: the local
    // player can sit in EITHER chair in PvP, so victory is judged
    // against gd.playerIdx — the old `winnerId === 0` shorthand is
    // only valid for NPC matches (human is always seat 0 there).
    // winnerId === null means the match was abandoned mid-duel (the
    // exit button, not the victory screen) — nothing is recorded,
    // so quitting can never farm the participation crest.
    // Each client records only its OWN result on its OWN profile.
    //
    // ⭐ N-SEAT FIX. `winnerId` is a raw seat idx in 1v1/FFA but a TEAM
    // id (a string) in 2v2 — `winnerId === gd.playerIdx` silently never
    // matches for a winning 2v2 seat whose own idx isn't literally the
    // team string. Shatterfield now hands back exitOpts.iWon — computed
    // there via didSeatWin(match, playerIdx), which already knows how to
    // read a seat OR a team out of match.winner — so that is used when
    // present. The raw comparison stays only as a fallback for any older
    // call site that hasn't been updated (there shouldn't be any left,
    // but a fallback that quietly reduces to the historical behavior
    // costs nothing). It also covers a concede in an undecided 3-4p FFA
    // — the match keeps going, so there's no winnerId at all yet, but
    // iWon:false still says plainly that THIS client lost.
    const gd = gameDataRef.current;
    const hasIWon = typeof exitOpts?.iWon === "boolean";

    // ── THE CO-OP LEDGER (2026-09-06) ─────────────────────────────
    // Gauntlet — Duo and Story Co-op used to fall through to the PvP
    // block below and pay BOTH humans a ladder win, crests and score
    // for beating an AI — a farm — while the Gauntlet run and the story
    // ledger recorded nothing (§6.5 #3 said "only the host's advances";
    // it was nobody's). Each client stamps ITS OWN uid: rules let no one
    // write another player's profile, so the host cannot stamp the
    // partner and does not try. The partner's Gauntlet bout answers
    // "no_run" when they have no run of their own — that is the
    // ordinary case, not an error.
    if (gd && gd.coopMode && (winnerId != null || hasIWon)) {
      const uid = auth?.currentUser?.uid;
      const localWon = hasIWon ? exitOpts.iWon : (winnerId === gd.playerIdx);
      if (uid) {
        // The run the boss was drawn from is the HOST's (resolveAiOpponent
        // reads the host's profile). Only the host stamps a bout — a
        // partner with a run of their own must not have it advanced, or
        // cleared on a loss, by a boss they never drew.
        if (gd.coopMode === "gauntlet2v2" && gd.playerIdx === 0) {
          try {
            const { recordBout } = await import("./gauntlet");
            let endAnima = 0;
            try {
              const { get: fbGet, ref: fbRef } = await import("firebase/database");
              const { db: fbDb } = await import("./firebase");
              // Pooled: the team's life lives at teamAnima/players.
              const snap = await fbGet(fbRef(fbDb, `matches/${gd.code}/teamAnima/players`));
              if (snap.exists()) endAnima = Number(snap.val()) || 0;
            } catch (e) {}
            const boutRes = await recordBout(uid, { won: localWon, endAnima });
            if (boutRes && boutRes.outcome !== "no_run") setGauntletResult(boutRes);
          } catch (e) { console.error("[Gauntlet Duo bout] error:", e); }
        } else if (gd.coopMode === "story-coop" && localWon && gd.aiMeta?.storyOpponentId) {
          try {
            const result = await recordStoryWin(uid, gd.aiMeta.storyOpponentId, gd.aiMeta.storyDungeonId ?? null);
            const crests = result?.crests || COIN_REWARDS.NPC_WIN;
            setPvpResult({ won: true, crests, coop: true, opponentName: gd.aiMeta?.name || "the boss" });
          } catch (e) { console.error("[Story Co-op] story stamp error:", e); }
        }
      }
      setScreen("plaza");
      gameDataRef.current = null;
      setGameData(null);
      try { localStorage.removeItem("terror_live_match"); } catch (e) {}
      return;
    }

    if (gd && !gd.isNPC && (winnerId != null || hasIWon)) {
      const uid = auth?.currentUser?.uid;
      if (uid) {
        const localWon = hasIWon ? exitOpts.iWon : winnerId === gd.playerIdx;
        try {
          // C16: a concede rides through as a crestless loss.
          const res = await recordPvpResult(uid, localWon, { conceded: !!exitOpts.conceded });
          if (res) setPvpResult({ won: localWon, ...res });
        } catch (e) {
          console.warn("[recordPvpResult] failed:", e);
        }
      }
    }

    setScreen("plaza");
    gameDataRef.current = null;
    setGameData(null);
    try { localStorage.removeItem("terror_live_match"); } catch (e) {}
  };

  // ── THE DOCS ROOMS — treated (C5, 2026-08-18) ───────────────
  // Same wrap as every room: equipped backdrop underlay, menu, chat,
  // stage hairline. docsProfile may be null (e.g. arriving before
  // login) — the overlay then paints the default backdrop and hides
  // the account chrome, which is exactly the pre-treatment behavior.
  if (screen === "howtoplay") return (
    <RoomOverlay profile={docsProfile} onExitToPlaza={() => { setScreen("plaza"); setDocsProfile(null); }}>
    <HowToPlay
      onBack={goBack}
      onRulebook={() => goTo("rulebook")}
      playerName={docsProfile?.name || "Aenarchist"}
    />
    </RoomOverlay>
  );
  if (screen === "deckbuilder") return (
    <RoomOverlay profile={docsProfile} onExitToPlaza={() => { setScreen("plaza"); setDocsProfile(null); }}>
    <DeckBuilder
      onBack={goBack}
    />
    </RoomOverlay>
  );
  if (screen === "rulebook")    return (
    <RoomOverlay profile={docsProfile} onExitToPlaza={() => { setScreen("plaza"); setDocsProfile(null); }}>
    <Rulebook onBack={goBack} />
    </RoomOverlay>
  );
  if (screen === "debugger")    return <Debugger onBack={goBack} />;
  if (screen === "storyreward" && storyReward) return (
    <StoryRewardScreen
      reward={storyReward}
      onContinue={() => { const back = storyReward?.returnToDungeon ? "dungeon1" : "plaza"; setStoryReward(null); setScreen(back); }}
    />
  );
  if (screen === "game" && (gameData?.code || gameDataRef.current?.code)) {
    const activeGameData = gameData || gameDataRef.current;
    return (
      <Shatterfield
        code={activeGameData.code}
        playerIdx={activeGameData.playerIdx}
        playerName={activeGameData.playerName}
        isNPC={activeGameData.isNPC || false}
        onExit={handleExit}
        collection={gameCollection}
      />
    );
  }

  // ── WILLIE'S WORKSHOP ────────────────────────────────────────
  if (screen === "workshop" && workshopProfile) return (
    <RoomOverlay profile={workshopProfile} onExitToPlaza={() => { setScreen("plaza"); setWorkshopProfile(null); }}>
    <WilliesWorkshop
      profile={workshopProfile}
      onExit={() => { setScreen("plaza"); setWorkshopProfile(null); }}
      onForge={(prof) => { setForgeProfile(prof); goTo("forge"); }}
      onCodex={(prof) => { setCollectionProfile(prof); setCodexChoice(true); }}
      onCheatChest={() => { setDocsProfile(workshopProfile); goTo("deckbuilder"); }}
      onLibrary={(prof) => { setLibraryProfile(prof || workshopProfile); goTo("library"); }}
    />
    {codexChoice && (
      <div onClick={() => setCodexChoice(false)} style={{
        position: "fixed", inset: 0, background: "rgba(4,2,10,0.9)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 600, backdropFilter: "blur(6px)",
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          width: "min(90vw, 380px)", padding: 22, borderRadius: 14,
          background: "#0d0a16", border: "1px solid #4a2f7c88",
          boxShadow: "0 12px 40px rgba(0,0,0,0.7)",
          fontFamily: "Georgia, serif", textAlign: "center",
        }}>
          <div style={{ fontSize: 11, letterSpacing: 4, color: "#8a8296", marginBottom: 16 }}>
            WHICH ARCHIVE?
          </div>
          <button onClick={() => { setCodexChoice(false); goTo("collection"); }} style={{
            width: "100%", padding: "14px 16px", marginBottom: 10, cursor: "pointer",
            background: "linear-gradient(135deg, rgba(212,168,75,0.16), transparent 60%)",
            border: "1px solid #d4a84b88", borderRadius: 10,
            color: "#f0cc60", fontFamily: "Georgia, serif", textAlign: "left",
          }}>
            <div style={{ fontSize: 15, fontWeight: 900, fontStyle: "italic", letterSpacing: 1 }}>◆ The Codex</div>
            <div style={{ fontSize: 10, color: "#b8a888", marginTop: 4, lineHeight: 1.5 }}>
              The Grand Archive — everything real. Your collection, story, records.
            </div>
          </button>
          <button onClick={() => { setCodexChoice(false); goTo("fauxdex"); }} style={{
            width: "100%", padding: "14px 16px", cursor: "pointer",
            background: "linear-gradient(135deg, rgba(138,95,216,0.16), transparent 60%)",
            border: "1px solid #8a5fd888", borderRadius: 10,
            color: "#c9b3ff", fontFamily: "Georgia, serif", textAlign: "left",
          }}>
            <div style={{ fontSize: 15, fontWeight: 900, fontStyle: "italic", letterSpacing: 1 }}>✦ The Fauxdex</div>
            <div style={{ fontSize: 10, color: "#9a8ab8", marginTop: 4, lineHeight: 1.5 }}>
              The Experimental hall — every imagined card, viewable only. Pure hypothesis.
            </div>
          </button>
        </div>
      </div>
    )}
    </RoomOverlay>
  );

  // ── WILLEE'S LIBRARY — beneath the Workshop ─────────────────
  // Reached only via the left-door bubble in Willie's Workshop.
  // Back button returns UPSTAIRS to the Workshop, not the Plaza.
  if (screen === "library" && libraryProfile) return (
    <WilleesLibrary
      profile={libraryProfile}
      onExit={() => { setWorkshopProfile(libraryProfile); setScreen("workshop"); setLibraryProfile(null); }}
    />
  );

  // ── FACTION HALL ─────────────────────────────────────────────
  if (screen === "faction" && factionProfile) return (
    <RoomOverlay profile={factionProfile} onExitToPlaza={() => { setScreen("plaza"); setFactionProfile(null); }}>
    <FactionHall
      profile={factionProfile}
      playerName={factionProfile?.name || "Aenarchist"}
      uid={factionProfile?.uid}
      onExit={() => { setScreen("plaza"); setFactionProfile(null); }}
    />
    </RoomOverlay>
  );


  // ── THE FORGE — Loadout builder (owned cards only) ─────────
  // Resolve the best available deck for the Forge to pre-populate:
  // 1. Firebase loadout (explicitly saved) 2. localStorage 3. Starter deck
  const resolveForgeInitialDeck = (prof) => {
    if (!prof) return null;
    if (prof?.loadout?.grimoire) return prof.loadout;
    // Try localStorage
    try {
      const saved = JSON.parse(localStorage.getItem("terror_tcg_grimoires") || "{}");
      if (prof?.starterDeckId && saved[prof.starterDeckId]) return saved[prof.starterDeckId];
    } catch(e) {}
    // Fall back to starter deck definition
    return prof?.starterDeckId ? getStarterDeck(prof.starterDeckId) : null;
  };
  if (screen === "forge" && forgeProfile) return (
    <RoomOverlay
      profile={forgeProfile}
      sections={[{
        id: "navigate", label: "Navigate", icon: "⌖",
        items: [
          { id: "to_workshop", label: "Return to Workshop", run: () => { setScreen("workshop"); setForgeProfile(null); } },
          { id: "to_plaza",    label: "Return to Plaza",    run: () => { setScreen("plaza"); setForgeProfile(null); setWorkshopProfile(null); } },
        ],
      }]}
    >
    <DeckBuilder
      onBack={() => { setScreen("workshop"); setForgeProfile(null); }}
      profile={forgeProfile}
      initialDeck={resolveForgeInitialDeck(forgeProfile)}
      onSaveLoadout={async (loadoutData, slot = null) => {
        const name = forgeProfile?.uid;
        // Save to Firebase without the synthetic collection (it's already in the profile)
        const { collection: _col, ...loadoutToSave } = loadoutData;
        // THE SETS (2026-08-17). A slot means the Forge is saving into
        // one of the three named sets, and `saveSet` owns the mirror
        // onto `loadout` from there. No slot means a caller that
        // predates sets, and it keeps the old direct write — the two
        // paths are deliberately not merged, because merging them
        // would make `saveLoadout` a second writer of the mirror and
        // that is the one thing THE MIRROR LAW forbids.
        if (name) {
          if (slot === null || slot === undefined) {
            await saveLoadout(name, loadoutToSave);
          } else {
            // Save THEN equip, in that order and always both. The
            // button says "Equip" and a button that saved without
            // equipping would be a lie the player only discovers
            // when they walk into a duel with the wrong deck.
            // saveSet writes the slot; equipSet moves the pointer
            // and refreshes the mirror — so editing set 3 while
            // set 1 is equipped does exactly what the tap looked
            // like it did.
            await saveSet(name, slot, loadoutToSave);
            await equipSet(name, slot);
          }
        }
        // Capture holo collection so next duel uses the right foils
        setGameCollection(loadoutData.collection || forgeProfile?.collection || {});
      }}
    />
    </RoomOverlay>
  );

  // ── PACK SHOP ────────────────────────────────────────────────
  // ⭐ "Return to Plaza" RETURNS TO THE PLAZA. Both this room and the
  // Codex passed `goBack()` to onExitToPlaza, and goBack() restores
  // whatever screen you came FROM — so a player who opened the shop
  // from Willie's Workshop pressed a leaf that says Return to Plaza
  // and landed in the Workshop. The room's own ← BACK button keeps
  // goBack(), which is what BACK means; the MENU leaf now does what
  // its label says.
  // ⭐ NO ROOMOVERLAY (caP'n, 2026-09-23): talking to Venus is a
  // conversation — no backdrop, no menu, only ← Back. See NpcStage.
  if (screen === "shop" && shopProfile) return (
    <PackShopScreen
      profile={shopProfile}
    playerName={shopProfile?.uid}
      onBack={() => { goBack(); setShopProfile(null); }}
    />
  );

  // ── COLLECTION / CODEX ───────────────────────────────────────
  // ── THE EXCHANGE POST (2026-09-06) — trades and the Scrap Lane ──
  if (screen === "tradepost" && tradeProfile) return (
    <RoomOverlay profile={tradeProfile} onExitToPlaza={() => { setScreen("plaza"); setTradeProfile(null); setPendingTradeId(null); }}>
    <TradePost
      profile={tradeProfile}
      uid={tradeProfile.uid}
      playerName={tradeProfile.name || "Aenarchist"}
      initialTradeId={pendingTradeId}
      friends={(socialBridge?.friends || []).filter((f) => f.status === "accepted")}
      onInviteFriend={(fUid, id) => socialBridge?.invite ? socialBridge.invite(fUid, tradeInviteOpts(id)) : Promise.resolve()}
      onBack={() => { goBack(); setTradeProfile(null); setPendingTradeId(null); }}
    />
    </RoomOverlay>
  );

  if (screen === "collection" && collectionProfile) return (
    <RoomOverlay profile={collectionProfile} onExitToPlaza={() => { setScreen("plaza"); setCollectionProfile(null); }}>
    <Codex
      profile={collectionProfile}
      onBack={() => { goBack(); setCollectionProfile(null); }}
    />
    </RoomOverlay>
  );

  // ── THE FAUXDEX — experimental hypothesis, view-only ────────
  if (screen === "fauxdex" && collectionProfile) return (
    <RoomOverlay profile={collectionProfile} onExitToPlaza={() => { setScreen("plaza"); setCollectionProfile(null); }}>
    <Fauxdex onBack={() => { goBack(); setCollectionProfile(null); }} />
    </RoomOverlay>
  );

  // ── DUNGEON 1 — Outskirts of the Minerium ──────────────────
  // ── THE UNSTABLE WORLD — the true story mode ────────────────
  // The map itself holds no launch logic: it hands back the node,
  // the opponent it resolved (authored boss OR procedural floor),
  // and whether this run is going for the node's SECRET exit. The
  // brain hand-off is the same one-shot park the Depths uses —
  // buildNPCPlayerData consumes it on the other side.
  if (screen === "worldmap" && worldProfile) return (
    <RoomOverlay profile={worldProfile} onExitToPlaza={() => { setScreen("plaza"); setWorldProfile(null); }}>
    {/* THE APPROACH SCENE — over the map, before the board exists.
        It clears itself first and then runs the held launch, so a
        double-fire cannot start two duels. */}
    {mapScene && (
      <StoryCutscene
        key={mapScene.key}
        lines={mapScene.lines}
        speaker={mapScene.speaker}
        speakerTitle={mapScene.speakerTitle}
        accent={mapScene.accent}
        playerName={mapScene.playerName || worldProfile?.name || "Aenarchist"}
        onDone={() => {
          // ── THE CHAIN ────────────────────────────────────────
          // One scene may hand straight to the next: the trace of a
          // child is what sends the parents back to demand answers,
          // and a frame of world map between them would break it.
          // `then` is the already-built next scene, so nothing is
          // looked up at the moment of handover.
          const go = mapScene.run;
          const then = mapScene.then || null;
          // A held duel travels down the chain rather than being
          // dropped by it. Nothing carries both today — approach
          // scenes hold a launch and beats never do — and this is the
          // line that keeps that from becoming load-bearing.
          setMapScene(then ? { ...then, run: then.run || go } : null);
          if (!then && go) go();
        }}
      />
    )}
    <WorldMap
      playerName={worldProfile?.name || "Aenarchist"}
      uid={worldProfile?.uid}
      profile={worldProfile}
      deck={resolvePlayerDeck(worldProfile)}
      result={worldResult}
      onClearResult={() => setWorldResult(null)}
      onExit={() => { setScreen("plaza"); setWorldProfile(null); }}
      /* ⬡ THE INTERCEPTION. Same launcher as a node fight, minus the
         node: no clear to stamp, no shard to take, no secret to
         offer. V'ael's treat plays first and hands on to the Avatar's
         own approach — one chain, both skippable, because caP'n's
         ruling ends with *"you can skip the cutscene as always of
         course."* StoryCutscene's Escape already does that and the
         chain honours it scene by scene. */
      ambushBlocked={!!mapScene}
      onAmbush={async (ambush, opponent) => {
        const adeck = resolvePlayerDeck(worldProfile);
        if (!adeck) { console.warn("[ambush] no deck — skipped"); return; }
        const { setNextNpcBrain } = await import("./NPC");
        setNextNpcBrain({
          skill: (typeof opponent?.npcSkill === "number") ? opponent.npcSkill : 0.4,
          temperament: opponent?.temperament || null,
        });
        const launch = () => handleEnterNPC(
          worldProfile?.name || "Aenarchist",
          { grimoire: adeck.grimoire || {}, luxmoire: adeck.luxmoire || {}, curios: adeck.curios || [null, null], esclings: adeck.esclings || null },
          adeck.prophecy || null,
          null,
          opponent?.difficulty || "Champion",
          {
            isWorldAmbush: true,
            worldAmbushId: ambush.id,
            bossPhases:  opponent?.phases || null,
            bossName:    opponent?.name || "",
            bossTitle:   opponent?.title || "",
            bossAccent:  opponent?.accentColor || "#c8963c",
            bossOnWin:   opponent?.dialogue?.onWin || null,
            bossOnLoss:  opponent?.dialogue?.onLoss || null,
            npcDeckId:   opponent?.starterDeckId || null,
            storyUid:    worldProfile?.uid,
            difficulty:  opponent?.difficulty || "Champion",
          }
        );
        const foeScene = hasScene(opponent?.dialogue?.approach)
          ? scene({
              lines: opponent.dialogue.approach,
              speaker: opponent.name,
              speakerTitle: opponent.title || "",
              accent: opponent.accentColor || "#c8963c",
              playerName: STORY_PLAYER,
              run: launch,
            })
          : null;
        const treat = ambush.treat;
        if (hasScene(treat?.lines)) {
          setMapScene(scene({
            lines: treat.lines,
            speaker: treat.speaker || "",
            speakerTitle: treat.speakerTitle || "",
            accent: treat.accent || "#c9a84c",
            playerName: STORY_PLAYER,
            run: foeScene ? null : launch,
            then: foeScene,
          }));
        } else if (foeScene) {
          setMapScene(foeScene);
        } else {
          launch();
        }
      }}
      onFight={async (node, opponent, secret) => {
        const deck = resolvePlayerDeck(worldProfile);
        // ⚠ A SILENT BOUNCE IS THE WORST FAILURE A DOOR CAN HAVE. This
        // used to return the player to the Plaza with no message at
        // all, which reads as the button being broken. With the starter
        // deck now in resolvePlayerDeck this should be unreachable —
        // but it is the last line between a player and a blank screen,
        // so it says something.
        if (!deck) {
          console.warn("[worldMap] no deck could be resolved for", worldProfile?.uid);
          setScreen("plaza");
          return;
        }
        const { setNextNpcBrain } = await import("./NPC");
        setNextNpcBrain({
          skill: (typeof opponent?.npcSkill === "number") ? opponent.npcSkill : 0.4,
          temperament: opponent?.temperament || null,
        });
        // THE APPROACH SCENE — if this opponent has words, they are said
        // before the board exists. `launch` is the duel, deferred whole,
        // so nothing about how a duel starts leaks into the cutscene.
        const launch = () => handleEnterNPC(
          worldProfile?.name || "Aenarchist",
          { grimoire: deck.grimoire || {}, luxmoire: deck.luxmoire || {}, curios: deck.curios || [null, null], esclings: deck.esclings || null },
          deck.prophecy || null,
          null,
          opponent?.difficulty || node.difficulty || "Beb",
          {
            isWorldMap:  true,
            worldNodeId: node.id,
            worldSecret: !!secret,
            vanguardTier: vanguardTierFor(node),
            // THE SECOND WIND — a multi-phase boss carries its phases
            // on the opponent; the launcher just forwards them.
            bossPhases:  opponent?.phases || null,
            // Carried so the AFTERMATH scene needs no second lookup and
            // cannot disagree with the opponent the duel actually used.
            bossName:    opponent?.name || "",
            bossTitle:   opponent?.title || "",
            bossAccent:  opponent?.accentColor || "#c8963c",
            bossOnWin:   opponent?.dialogue?.onWin || null,
            bossOnLoss:  opponent?.dialogue?.onLoss || null,
            // ⭐ THE ZONE'S ILK LAW, carried to the table. See the deck
            // block in handleEnterNPC for why this field had to exist.
            npcDeckId:   opponent?.starterDeckId || null,
            storyUid:    worldProfile?.uid,
            difficulty:  opponent?.difficulty || node.difficulty || "Beb",
          }
        );
        if (hasScene(opponent?.dialogue?.approach)) {
          setMapScene(scene({
            lines: opponent.dialogue.approach,
            speaker: opponent.name,
            speakerTitle: opponent.title || "",
            accent: opponent.accentColor || "#c8963c",
            playerName: STORY_PLAYER,
            run: launch,
          }));
        } else {
          launch();
        }
      }}
    />
    </RoomOverlay>
  );

  if (screen === "dungeon1") return (
    <RoomOverlay profile={dungeonProfile} onExitToPlaza={goBack}>
    <Dungeon1
      profile={dungeonProfile}
      onExit={goBack}
      onBossBattle={(opponentId) => {
        // ⭐ resolvePlayerDeck, NOT a fourth hand-rolled copy of the
        // chain. This branch retyped loadout -> saved -> first-saved and
        // stopped there, WITHOUT the starter-deck term added to the
        // shared resolver — while the comment underneath told the next
        // reader that the starter fallback covers this door. It did not.
        // Dungeon 1 is parked today, but it is a shipping screen with a
        // live router branch and whoever wakes it would have trusted a
        // false note and got the silent bounce back.
        const deck = resolvePlayerDeck(dungeonProfile);
        // ⚠ A SILENT BOUNCE IS THE WORST FAILURE A DOOR CAN HAVE. This
        // used to return the player to the Plaza with no message at
        // all, which reads as the button being broken. With the starter
        // deck now in resolvePlayerDeck this should be unreachable —
        // but it is the last line between a player and a blank screen,
        // so it says something. (And it logs the profile it actually
        // failed on, which the first draft of this line did not.)
        if (!deck) {
          console.warn("[dungeon1] no deck could be resolved for", dungeonProfile?.uid);
          setScreen("plaza");
          return;
        }
        const diffMap = { suhnker: "Champion", zhefir: "Champion", tyrent: "Lord" };
        handleEnterNPC(
          dungeonProfile?.name || "Aenarchist",
          { grimoire: deck.grimoire || {}, luxmoire: deck.luxmoire || {}, curios: deck.curios || [null, null], esclings: deck.esclings || null },
          deck.prophecy || null,
          null,
          diffMap[opponentId] || "Champion",
          {
            storyOpponentId: opponentId,
            // storyUid — renamed from storyPlayerName (July 3): this field
            // has carried a Firebase Auth uid (not a display name) since the
            // Profile-Key Seal; the old name was misleading. Same value,
            // honest name. All readers renamed in the same deploy.
            storyUid: dungeonProfile?.uid,
            storyDungeonId: 1,
            returnToDungeon: true,
          }
        );
      }}
    />
    </RoomOverlay>
  );

  // ── UNSTABLE PIERRE ──────────────────────────────────────────
  if (screen === "pierre" && pierreProfile) return (
    <UnstablePierre
      playerName={pierreProfile?.name || "Aenarchist"}
      playerUid={pierreProfile?.uid || ""}
      profile={pierreProfile}
      onEnterGame={(portal) => {
        if (portal.id === "terror") setScreen("plaza");
        // Add future games here as they're built
      }}
      onBack={() => setScreen("plaza")}
    />
  );

  // ── PLAZA ────────────────────────────────────────────────────
  if (screen === "plaza") return (
    <Plaza
      inviteCode={pendingInviteCode}
      onInviteConsumed={() => setPendingInviteCode(null)}
      pendingTradeId={pendingTradeId}
      onEnterDungeon={(portal, profile) => {
        // ── THE HANDOVER (July 28, 2026) ─────────────────────
        // Dungeon 1 is at REST by caP'n's decree — not deleted:
        // Dungeon1.js, its save data and its secret stamps are all
        // untouched, and adding `case "dungeon1": goTo("dungeon1")`
        // below plus one field on one row in plazaZones.js restores
        // it. The blue arch opens the true story mode.
        //
        // ── ROUTE, NOT ID (July 29, 2026) ────────────────────
        // This used to read `if (portal.id === "space")`. "space"
        // was Dungeon 1's door, so after the handover the Story Mode
        // arch was secretly named after a dungeon that no longer
        // opens — an implementation detail wearing canon's clothes,
        // and the kind of thing that reads as deliberate to whoever
        // finds it next. Portals now declare a `route` and this
        // switch reads it, so the next door is a row in the zones
        // table instead of another branch in here.
        switch (portal?.route) {
          case "worldmap":
            // ⬡ THE DOOR. Some Mineriums have a doorman, and his fight
            // happens BEFORE the map exists — so it is gated here, at
            // the one route into the world, rather than inside a map
            // that should not have to know it might not be allowed to
            // open. If the gate is answered this is the old two lines.
            (async () => {
              try {
                const prog = await loadWorldProgress(profile?.uid);
                const gate = pendingGate(prog);
                const foe = gate ? opponentForGate(gate) : null;
                if (!gate || !foe) { setWorldProfile(profile); goTo("worldmap"); return; }

                const deck = resolvePlayerDeck(profile);
                if (!deck) { setWorldProfile(profile); goTo("worldmap"); return; }

                const { setNextNpcBrain } = await import("./NPC");
                setNextNpcBrain({
                  skill: (typeof foe.npcSkill === "number") ? foe.npcSkill : 0.35,
                  temperament: foe.temperament || null,
                });

                const launch = () => handleEnterNPC(
                  profile?.name || "Aenarchist",
                  { grimoire: deck.grimoire || {}, luxmoire: deck.luxmoire || {},
                    curios: deck.curios || [null, null], esclings: deck.esclings || null },
                  deck.prophecy || null,
                  null,
                  foe.difficulty || "Beb",
                  {
                    isWorldGate: true,
                    worldGateId: gate.id,
                    storyUid:    profile?.uid,
                    difficulty:  foe.difficulty || "Beb",
                    npcDeckId:   foe.starterDeckId || null,
                    bossPhases:  foe.phases || null,
                    bossName:    foe.name || "",
                    bossTitle:   foe.title || "",
                    bossAccent:  foe.accentColor || "#c8963c",
                    bossOnWin:   foe.dialogue?.onWin || null,
                    bossOnLoss:  foe.dialogue?.onLoss || null,
                    gateProfile: profile,
                  }
                );

                setWorldProfile(profile);   // so the scene has a map behind it
                if (hasScene(foe.dialogue?.approach)) {
                  setMapScene(scene({
                    lines: foe.dialogue.approach,
                    speaker: foe.name, speakerTitle: foe.title || "",
                    accent: foe.accentColor || "#c8963c", run: launch,
                    playerName: STORY_PLAYER,
                  }));
                  goTo("worldmap");
                } else {
                  launch();
                }
              } catch (e) {
                // A gate that cannot be READ must never lock a player out
                // of their own world. Fail OPEN, loudly.
                console.warn("[worldGate] check failed — opening the world:", e?.message);
                setWorldProfile(profile);
                goTo("worldmap");
              }
            })();
            break;
          default:
            console.warn("[Plaza] portal has no route:", portal?.id);
        }
      }}
      onEnterNPC={handleEnterNPC}
      onEnterGame={handleEnterGame}
      onDeckBuilder={(prof) => { setDocsProfile(prof || null); goTo("deckbuilder"); }}
      // ⭐ THE RULEBOOK ROW NOW OPENS THE RULEBOOK. Both of these used
      // to `goTo("howtoplay")` — so the menu's "The Rulebook" leaf
      // opened the TUTORIAL hub, the real Rulebook sat one level
      // deeper behind a button inside it, and two front-door rows
      // pointed at the same screen. Rulebook.js is the largest
      // player-facing document in the game (150KB) and had no door of
      // its own anywhere. HowToPlay keeps its own link across, so the
      // path that existed still exists; this adds the direct one.
      onRulebook={(prof) => { setDocsProfile(prof || null); goTo("rulebook"); }}
      onTutorial1={(prof) => { setDocsProfile(prof || null); goTo("howtoplay"); }}
      // The Debugger has had a router branch and no door in the entire
      // life of the project — 56KB of rule simulator nobody could open
      // without editing code. Dev builds only; see the Settings section
      // in Plaza.js for why it is gated rather than hidden.
      onDebugger={(prof) => { setDocsProfile(prof || null); goTo("debugger"); }}
      onCollection={(prof) => { setCollectionProfile(prof); goTo("collection"); }}
      onTrading={(prof) => { setTradeProfile(prof); goTo("tradepost"); }}
      onEnterShop={(prof) => { setShopProfile(prof); goTo("shop"); }}
      onForge={(prof) => { setWorkshopProfile(prof || null); goTo("workshop"); }}
      onFactionHall={(prof) => { setFactionProfile(prof || null); goTo("faction"); }}
      // ⭐ THE RECORD GETS A FRONT DOOR. Willee's Library held 48 books
      // and was two levels deep behind a bubble on the left wall of
      // Willie's Workshop: Plaza -> menu -> The Forge -> notice the
      // door -> walk to it. Most players were never going to find it,
      // and it is the single largest piece of authored lore in Terror.
      // The in-world door STAYS -- discovering it by walking is the
      // better experience and nothing about it changed. This is the
      // second way in, for the player who already knows it is there.
      // Exit still returns UPSTAIRS to the Workshop, which is correct:
      // the Library is under the Workshop no matter how you arrived.
      onLibrary={(prof) => { setLibraryProfile(prof || null); goTo("library"); }}
      onPierre={(prof) => {
        // ── Pierre handoff ───────────────────────────────────────
        // Pierre is its own deployed app at unstable-pierre.pages.dev.
        // We pass the uid in the URL so Pierre can skip its login
        // screen and load the same profile. Firebase Auth persists
        // per-domain so returning to Terror keeps the session alive.
        const outUid = prof?.uid || "";
        if (!outUid) { console.warn("[Pierre handoff] no uid available — aborting"); return; }
        window.location.href = `https://unstable-pierre.pages.dev?uid=${encodeURIComponent(outUid)}`;
      }}
      onBack={() => setScreen("menu")}
      depthResult={depthResult}
      onClearDepthResult={() => setDepthResult(null)}
      gauntletResult={gauntletResult}
      onClearGauntletResult={() => setGauntletResult(null)}
      pvpResult={pvpResult}
      onClearPvpResult={() => setPvpResult(null)}
    />
  );

  // ── BOOT / TITLE — cinematic plays on launch, Enter → Plaza ──
  // Plaza owns authentication (LoginOverlay). The boot sequence is
  // the title cinematic; when it completes or is skipped we land on
  // Plaza, which handles login and is the true app shell.
  return (
    <BootSequence onComplete={() => setScreen("plaza")} />
  );
}